/**
 * Authentication Service
 * Handles Google OAuth authentication and user session management
 */

import { GoogleCredentialResponse } from '@react-oauth/google';
import { validateGoogleToken } from './auth-backend.service';
import { getUserById } from './user.service';
import { ApiError } from './api.client';

export interface User {
  id: string; // Google sub
  email: string;
  name: string;
  picture?: string;
  idToken?: string;
  country?: string;
  state?: string;
  role?: string;
  company?: string;
  onboarded?: boolean;
  credits?: number;
  subscriptionStatus?: 'active' | 'inactive' | 'cancelled';
  subscriptionId?: string;
  stripeCustomerId?: string;
  subscriptionEndDate?: Date;
}

/**
 * Handle successful Google authentication
 * Validates the token with both Google and our backend, then fetches user data
 */
export const handleGoogleSuccess = async (credentialResponse: GoogleCredentialResponse): Promise<User> => {
  try {
    if (!credentialResponse.credential) {
      throw new Error('No credential received from Google');
    }

    // Validate token with Google
    const googleResponse = await fetch(
      'https://oauth2.googleapis.com/tokeninfo?id_token=' + credentialResponse.credential
    );
    
    if (!googleResponse.ok) {
      throw new Error('Failed to validate token with Google');
    }
    
    const googleData = await googleResponse.json();
    
    // Create initial user object
    const user: User = {
      id: googleData.sub,
      email: googleData.email,
      name: googleData.name,
      picture: googleData.picture,
      idToken: credentialResponse.credential,
    };

    // Validate with backend and get additional user data
    try {
      const backendUser = await validateGoogleToken(credentialResponse.credential);
      
      // Try to get full user profile from backend
      try {
        const userProfile = await getUserById(backendUser.sub);
        
        // Merge backend user data with Google data
        user.country = userProfile.country;
        user.state = userProfile.state;
        user.role = userProfile.role;
        user.company = userProfile.company;
        user.onboarded = userProfile.onboarded;
        user.credits = userProfile.credits;
        user.subscriptionStatus = userProfile.subscriptionStatus;
        user.subscriptionId = userProfile.subscriptionId;
        user.stripeCustomerId = userProfile.stripeCustomerId;
        user.subscriptionEndDate = userProfile.subscriptionEndDate;
      } catch (error) {
        if (error instanceof ApiError && error.statusCode === 404) {
          // User not found in backend yet, that's okay
          console.log('User not found in backend, will need onboarding');
          user.onboarded = false;
          user.credits = 0;
        } else {
          // Re-throw other errors (like 502) to be handled by the outer catch
          throw error;
        }
      }
    } catch (error) {
      console.error('Backend validation failed:', error);
      throw error; // Propagate the error to the UI
    }

    // Store user in localStorage
    localStorage.setItem('user', JSON.stringify(user));
    return user;
  } catch (error) {
    console.error('Error handling Google sign-in:', error);
    throw error;
  }
};

/**
 * Handle Google authentication error
 */
export const handleGoogleError = (error: Error) => {
  console.error('Google sign-in error:', error);
  throw error;
};

/**
 * Handle user logout
 * Clears user data from localStorage
 */
export const handleGoogleLogout = () => {
  localStorage.removeItem('user');
  console.log('Google sign-out successful');
};

/**
 * Get current authenticated user from localStorage
 */
export const getCurrentUser = (): User | null => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return null;
  
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const user = getCurrentUser();
  return user !== null && !!user.idToken;
};

/**
 * Update user data in localStorage
 */
export const updateUser = (userData: Partial<User>): User | null => {
  const currentUser = getCurrentUser();
  if (!currentUser) return null;
  
  const updatedUser = { ...currentUser, ...userData };
  localStorage.setItem('user', JSON.stringify(updatedUser));
  return updatedUser;
};  
